import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Collapse, Grid, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '../common/Button';
import Card from '../common/Card';
import Modal from '../common/Modal';
import Typography from '@mui/material/Typography';
import { SectionHeading } from '../common/styled/SectionHeading';
import DeliveryInfoField from './DeliveryInfoField';
import { useTranslation } from 'react-i18next';
import { helpingMethod } from '../../utils/HelpingMethod';

interface DeliveryInfo {
  manager?: string;
  navilands: string;
  deliveryType?: string;
  standardDelivery: string;
  shellMarine: string | number | null;
  port?: string;
  usdProvision: string;
  extraCosts?: any[];
  extraCostsValues?: any;
  poNo: string;
  invoiceNo: string;
  // New fields for Adjusting delivery type
  managementPeriod?: string;
}

interface DeliveryInformationCardProps {
  deliveryInfo: DeliveryInfo;
  deliveryType?: string;
  onBack: () => void;
  managerOptions?: any[];
  vesselOptions?: any[];
  availableDates?: { startDate: string; endDate: string; id?: number | null };
  supplierOptions?: any[];
  portOptions?: any[];
  extraCostsOptions?: any[];
}

const DeliveryInformationCard: React.FC<DeliveryInformationCardProps> = ({
  deliveryInfo,
  deliveryType,
  onBack,
  managerOptions,
  vesselOptions,
  availableDates,
  supplierOptions,
  portOptions,
  extraCostsOptions,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isExtraCostsModalOpen, setIsExtraCostsModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);

  return (
    <Card
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          mb: expanded ? 1.5 : 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          width: '100%',
          transition: `margin-bottom ${theme.transitions.custom.slow}`,
        }}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <SectionHeading>{t('deliveries.deliveryInformation')}</SectionHeading>

        <Box
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Button variant="outlinedBlue" onClick={onBack} sx={{ fontSize: '12px' }}>
            {t('buttons.edit')}
          </Button>
          <IconButton
            size="small"
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: `transform ${theme.transitions.custom.default}`,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
          >
            <ExpandMoreIcon sx={{ color: theme.palette.inputField.font }} />
          </IconButton>
        </Box>
      </Box>

      <Collapse
        in={expanded}
        timeout={500}
        easing={{
          enter: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
          exit: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
        }}
        sx={{
          width: '100%',
          '& .MuiCollapse-wrapperInner': {
            willChange: 'height',
          },
        }}
      >
        <div>
          {/* Custom divider that spans full width */}
          <Box
            sx={{
              height: '2px',
              backgroundColor: theme.palette.inputField.border,
              mb: 3,
              mx: -2,
              width: 'calc(100% + 32px)',
            }}
          />

          {deliveryType === 'adjusting' ? (
            // Adjusting Delivery Information
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 1.7 }}>
                <DeliveryInfoField
                  label={t('form.managerName')}
                  value={(() => {
                    const managerValue = String(deliveryInfo.manager);
                    const manager = managerOptions?.find((m) => String(m.value) === managerValue);
                    return manager?.label || deliveryInfo.manager || t('common.dash');
                  })()}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 1.3 }}>
                <DeliveryInfoField
                  label={t('common.vessel')}
                  value={(() => {
                    const vesselValue = String(deliveryInfo.navilands);
                    const vessel = vesselOptions?.find((v) => String(v.value) === vesselValue);
                    return vessel?.label || deliveryInfo.navilands || t('common.dash');
                  })()}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 1.7 }}>
                <DeliveryInfoField
                  label={t('form.deliveryType')}
                  value={(() => {
                    const typeLabels: Record<string, string> = {
                      standard: t('deliveries.standard'),
                      adjusting: t('deliveries.adjusting'),
                      secondHand: t('deliveries.secondHand'),
                      newBuilding: t('deliveries.newBuilding'),
                    };
                    return (
                      typeLabels[deliveryInfo.deliveryType || ''] ||
                      deliveryInfo.deliveryType ||
                      t('common.dash')
                    );
                  })()}
                  sx={{ textTransform: 'capitalize' }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2.5 }}>
                <DeliveryInfoField
                  label={t('form.managementPeriod')}
                  value={
                    availableDates?.startDate +
                    ' ' +
                    t('common.between') +
                    ' ' +
                    (availableDates?.endDate || t('common.present'))
                  }
                />
              </Grid>

              <Grid size={{ xs: 12, md: 1.8 }}>
                <DeliveryInfoField
                  label={t('form.date')}
                  value={helpingMethod.formatDate(deliveryInfo.standardDelivery)}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <DeliveryInfoField
                  label={t('common.remarks')}
                  value={deliveryInfo.shellMarine || t('common.dash')}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                />
              </Grid>
            </Grid>
          ) : deliveryType === 'secondHand' ? (
            // 2nd Hand Vessel Acquisition Delivery Information
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 1.8 }}>
                <DeliveryInfoField
                  label={t('form.managerName')}
                  value={(() => {
                    const managerValue = String(deliveryInfo.manager);
                    const manager = managerOptions?.find((m) => String(m.value) === managerValue);
                    return manager?.label || deliveryInfo.manager || t('common.dash');
                  })()}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 1.8 }}>
                <DeliveryInfoField
                  label={t('common.vessel')}
                  value={(() => {
                    const vesselValue = String(deliveryInfo.navilands);
                    const vessel = vesselOptions?.find((v) => String(v.value) === vesselValue);
                    return vessel?.label || deliveryInfo.navilands || t('common.dash');
                  })()}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 1.8 }}>
                <DeliveryInfoField
                  label={t('form.deliveryType')}
                  value={(() => {
                    const typeLabels: Record<string, string> = {
                      standard: t('deliveries.standard'),
                      adjusting: t('deliveries.adjusting'),
                      secondHand: t('deliveries.secondHand'),
                      newBuilding: t('deliveries.newBuilding'),
                    };
                    return (
                      typeLabels[deliveryInfo.deliveryType || ''] ||
                      deliveryInfo.deliveryType ||
                      t('common.dash')
                    );
                  })()}
                  sx={{ textTransform: 'capitalize' }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <DeliveryInfoField
                  label={t('form.managementPeriod')}
                  value={
                    availableDates?.startDate +
                    ' ' +
                    t('common.between') +
                    ' ' +
                    (availableDates?.endDate || t('common.present'))
                  }
                />
              </Grid>

              <Grid size={{ xs: 12, md: 1.8 }}>
                <DeliveryInfoField label={t('form.currency')} value="USD" />
              </Grid>
            </Grid>
          ) : (
            // Standard/New Building Delivery Information
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 2.4 }}>
                <DeliveryInfoField
                  label={t('form.managerName')}
                  value={(() => {
                    const managerValue = String(deliveryInfo.manager);
                    const manager = managerOptions?.find((m) => String(m.value) === managerValue);
                    return manager?.label || deliveryInfo.manager || t('common.dash');
                  })()}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2.4 }}>
                <DeliveryInfoField
                  label={t('form.vesselName')}
                  value={(() => {
                    const vesselValue = String(deliveryInfo.navilands);
                    const vessel = vesselOptions?.find((v) => String(v.value) === vesselValue);
                    return vessel?.label || deliveryInfo.navilands || t('common.dash');
                  })()}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2.4 }}>
                <DeliveryInfoField
                  label={t('form.deliveryType')}
                  value={(() => {
                    const typeLabels: Record<string, string> = {
                      standard: t('deliveries.standard'),
                      adjusting: t('deliveries.adjusting'),
                      secondHand: t('deliveries.secondHand'),
                      newBuilding: t('deliveries.newBuilding'),
                    };
                    return (
                      typeLabels[deliveryInfo.deliveryType || ''] ||
                      deliveryInfo.deliveryType ||
                      t('common.dash')
                    );
                  })()}
                  sx={{ textTransform: 'capitalize' }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2.4 }}>
                <DeliveryInfoField
                  label={t('form.date')}
                  value={helpingMethod.formatDate(deliveryInfo.standardDelivery)}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2.4 }}>
                <DeliveryInfoField
                  label={t('form.supplierName')}
                  value={(() => {
                    const supplierValue = String(deliveryInfo.shellMarine);
                    const supplier = supplierOptions?.find(
                      (s) => String(s.value) === supplierValue,
                    );
                    return supplier?.label || deliveryInfo.shellMarine || t('common.dash');
                  })()}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2.4 }}>
                <DeliveryInfoField
                  label={t('form.portName')}
                  value={(() => {
                    const portValue = String(deliveryInfo.port);
                    const port = portOptions?.find((p) => String(p.value) === portValue);
                    return port?.label || deliveryInfo.port || t('common.dash');
                  })()}
                />
              </Grid>

              {/* Extra row for USD Provision and Extra Costs - Only for standard/newBuilding */}
              {deliveryType !== 'adjusting' && deliveryType !== 'secondHand' && (
                <>
                  <Grid size={{ xs: 12, md: 2.4 }}>
                    <DeliveryInfoField label={t('form.currency')} value={t('common.usd')} />
                  </Grid>

                  <Grid size={{ xs: 12, md: 2.4 }}>
                    <DeliveryInfoField
                      label={t('form.extraCosts')}
                      value={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {deliveryInfo.extraCosts &&
                          deliveryInfo.extraCosts.length > 0 &&
                          deliveryInfo.extraCostsValues ? (
                            <>
                              <Typography
                                sx={{
                                  color: theme.palette.inputField.font,
                                  fontSize: '16px',
                                  fontWeight: 400,
                                }}
                              >
                                {(() => {
                                  const firstCostId = deliveryInfo.extraCosts[0];
                                  const costOption = extraCostsOptions?.find(
                                    (option) => option.value === Number(firstCostId),
                                  );
                                  const fieldName = firstCostId.split('_').join('');
                                  const value = deliveryInfo.extraCostsValues?.[fieldName] || '0';
                                  return `${costOption?.label || `Cost ${firstCostId}`} (${value})`;
                                })()}
                              </Typography>
                              {deliveryInfo.extraCosts.length > 1 && (
                                <Typography
                                  component="span"
                                  onClick={() => setIsExtraCostsModalOpen(true)}
                                  sx={{
                                    color: theme.palette.primary.main,
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    '&:hover': {
                                      color: theme.palette.primary.dark,
                                    },
                                  }}
                                >
                                  +{deliveryInfo.extraCosts.length - 1}
                                </Typography>
                              )}
                            </>
                          ) : (
                            <Typography
                              sx={{
                                color: theme.palette.inputField.font,
                                fontSize: '16px',
                                fontWeight: 400,
                              }}
                            >
                              -
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 2.4 }}>
                    <DeliveryInfoField label={t('form.poNo')} value={deliveryInfo.poNo} />
                  </Grid>

                  <Grid size={{ xs: 12, md: 2.4 }}>
                    <DeliveryInfoField label={t('form.invoiceNo')} value={deliveryInfo.invoiceNo} />
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </div>
      </Collapse>

      {/* Extra Costs Modal */}
      <Modal
        open={isExtraCostsModalOpen}
        onClose={() => setIsExtraCostsModalOpen(false)}
        title={t('deliveries.allExtraCosts')}
        size="sm"
        footer={
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="blue" onClick={() => setIsExtraCostsModalOpen(false)}>
              {t('buttons.close')}
            </Button>
          </Box>
        }
      >
        <Box
          sx={{
            pt: 2,
            maxHeight: '60vh',
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.scrollbar.track,
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.scrollbar.thumb,
              borderRadius: '10px',
              '&:hover': {
                backgroundColor: theme.palette.scrollbar.thumbHover,
              },
            },
          }}
        >
          {deliveryInfo.extraCosts &&
          deliveryInfo.extraCosts.length > 0 &&
          deliveryInfo.extraCostsValues ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 1 }}>
              {deliveryInfo.extraCosts.map((costId: string) => {
                const costOption = extraCostsOptions?.find(
                  (option) => option.value === Number(costId),
                );
                const fieldName = costId.split('_').join('');
                const value = deliveryInfo.extraCostsValues?.[fieldName] || '0';
                return (
                  <Box
                    key={costId}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 2,
                      border: `1px solid ${theme.palette.inputField.border}`,
                      borderRadius: `${theme.borderRadius.md}px`,
                      transition: `all ${theme.transitions.custom.fast}`,
                      '&:hover': {
                        boxShadow: theme.customShadows.subtle,
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: theme.palette.inputField.font,
                        fontSize: '16px',
                        fontWeight: 500,
                      }}
                    >
                      {costOption?.label || `Cost ${costId}`}
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.inputField.font,
                        fontSize: '16px',
                        fontWeight: 600,
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography
              sx={{
                color: theme.palette.inputField.font,
                fontSize: '16px',
                textAlign: 'center',
                py: 4,
              }}
            >
              {t('deliveries.noExtraCosts')}
            </Typography>
          )}
        </Box>
      </Modal>
    </Card>
  );
};

export default DeliveryInformationCard;
